import { Button, Dialog, MessagePanel, RawDatePicker } from '@dittolive/anvil'
import { graphql } from 'babel-plugin-relay/macro'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useEffect, useState } from 'react'
import { RRNLRequestError } from 'react-relay-network-modern'
import { usePortalMutation } from 'utils'

import { OfflineLicenseTokenCreationModalCreateLicenseMutation } from './__generated__/OfflineLicenseTokenCreationModalCreateLicenseMutation.graphql'

dayjs.extend(localizedFormat)

const createLicenseTokenMutation = graphql`
  mutation OfflineLicenseTokenCreationModalCreateLicenseMutation(
    $appId: ID!
    $expiry: String!
  ) {
    createOfflineLicense(appId: $appId, expiry: $expiry) {
      id
      expiresAt
      createdAt
      app {
        id
        offlineLicenseRequestStatus
        licenses {
          id
          token
          expiresAt
          createdAt
        }
      }
    }
  }
`

type Props = {
  /** App for which the token is created. */
  appId: string
  show: boolean
  onClose: (didCreate: boolean) => void
}

/** Modal used by super admins for creating offline license tokens.*/
const OfflineLicenseTokenCreationModal = ({ appId, show, onClose }: Props) => {
  const [createOfflineLicense, isCreatingOfflineLicense] =
    usePortalMutation<OfflineLicenseTokenCreationModalCreateLicenseMutation>(
      createLicenseTokenMutation,
      {
        mutationName: 'createOfflineLicense',
        sentryContext: { component: 'OfflineLicenseTokenCreationModal' },
      },
    )
  const [expirationDate, setExpirationDate] = useState(
    dayjs().add(30, 'days').endOf('day').toDate(),
  )
  const [licenseCreationError, setLicenseCreationError] = useState<string>()

  const handleCloseModal = () => {
    onClose(false)
  }

  /** Joins all the error messages into one string, using a newline as the separator.
      If `err.res.errors` doesn't exist, it will default to `err.message`. */
  const readableErrorMessage = (err: RRNLRequestError) => {
    return err.res?.errors?.map((err) => err.message).join('\n') ?? err.message
  }

  const handleCreateOfflineLicense = () => {
    setLicenseCreationError(undefined)
    createOfflineLicense({
      variables: { appId, expiry: expirationDate.toISOString() },
      onCompleted: () => {
        onClose(true)
      },
      onError: (err: Error) => {
        if (err instanceof RRNLRequestError) {
          setLicenseCreationError(readableErrorMessage(err))
        } else {
          setLicenseCreationError(err.message)
        }
      },
    })
  }

  const isTooFarAway = (date: Date): boolean => {
    return dayjs(date).isAfter(getLastValidDate())
  }

  const isTooEarly = (date: Date): boolean => {
    return dayjs(date).isBefore(dayjs().toDate())
  }

  const getLastValidDate = (): Date => {
    // A year and a half in weeks
    return dayjs().add(78, 'weeks').subtract(1, 'day').endOf('day').toDate()
  }

  useEffect(() => {
    if (!show) {
      // reset the calendar whenever the modal closes
      setExpirationDate(dayjs().add(30, 'days').endOf('day').toDate())
    }
  }, [show])

  return (
    <Dialog open={show} onOpenChange={handleCloseModal}>
      <Dialog.Content size="md">
        <Dialog.Header>
          <Dialog.Title>New offline license token</Dialog.Title>
          <Dialog.Description>
            Choose an expiry date for your token
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Body className="space-y-4">
          {!!licenseCreationError && (
            <MessagePanel message={licenseCreationError} variant="danger" />
          )}
          <div className="flex items-center justify-start">
            <RawDatePicker
              selected={expirationDate}
              onMonthChange={(nextDate) => {
                if (isTooFarAway(nextDate)) {
                  setExpirationDate(getLastValidDate())
                } else if (isTooEarly(nextDate)) {
                  setExpirationDate(dayjs().toDate())
                } else {
                  setExpirationDate(dayjs(nextDate).endOf('day').toDate())
                }
              }}
              onChange={(nextDate) => {
                if (!!nextDate) {
                  setExpirationDate(dayjs(nextDate).endOf('day').toDate())
                }
              }}
              htmlFor="offlineLicenseExpirationDate"
              minDate={dayjs().toDate()}
              maxDate={getLastValidDate()}
              inline
            />
          </div>
        </Dialog.Body>

        <Dialog.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={isCreatingOfflineLicense}
            data-testid="confirmationDialogCancelButton"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateOfflineLicense}
            disabled={isCreatingOfflineLicense || isTooFarAway(expirationDate)}
            data-testid="confirmationDialogConfirmButton"
          >
            Create token
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default OfflineLicenseTokenCreationModal
